import { TourProps } from 'antd'
import { TOUR_TARGET_IDS } from './tour-targets'

export type TourStepDefinition = {
  targetId: string
  title: string
  description: string
  placement?: NonNullable<TourProps['steps']>[number]['placement']
  hideNextButton?: boolean
}

export const buildStepDefinitions = (studentPlanSectionEnabled?: boolean): TourStepDefinition[] => [
  {
    targetId: TOUR_TARGET_IDS.yourPlan,
    title: studentPlanSectionEnabled ? 'Your 6 activities' : 'Your next steps',
    description: studentPlanSectionEnabled
      ? 'These six activities are crucial for building confidence in your Career and preparing you for Careers appointments.'
      : 'A few activities chosen just for you, to help you feel confident and ready for your Careers appointments.'
  },
  {
    targetId: TOUR_TARGET_IDS.continueWhereLeftOff,
    title: 'Pick up where you left off',
    description: "Start an activity and it'll show up here, so you can jump back in anytime."
  },
  {
    targetId: TOUR_TARGET_IDS.chatHistory,
    title: 'Every chat, saved',
    description: 'All your chats are kept here on the left, ready to revisit anytime.',
    // The chat history sits on the left (desktop sidebar / mobile-mode drawer), both narrow, so the
    // tooltip points at it from the right. This also keeps it from being clipped on tablets, where the
    // target is taller than the viewport and a top/bottom placement would not fit.
    placement: 'right'
  },
  {
    targetId: TOUR_TARGET_IDS.exploreActivities,
    title: 'Need extra support?',
    description: 'Explore extra activities to support you in your Career journey.'
  },
  {
    targetId: TOUR_TARGET_IDS.firstActivityCard,
    title: "You're ready to begin",
    description: studentPlanSectionEnabled
      ? 'Click your Career Story Snapshot — the first activity in Module 1 — to start.'
      : 'Click your first activity to start.',
    hideNextButton: true
  }
]

// The definitions whose target element is currently in the DOM, in tour order. buildSteps and the
// tour hook both rely on this so the displayed step count and the hook's chat-history step index
// stay in sync (steps for absent targets are dropped, shifting later indices).
export const getVisibleStepDefinitions = (studentPlanSectionEnabled?: boolean): TourStepDefinition[] =>
  buildStepDefinitions(studentPlanSectionEnabled).filter(({ targetId }) => document.getElementById(targetId))

export const buildSteps = (studentPlanSectionEnabled?: boolean): TourProps['steps'] =>
  getVisibleStepDefinitions(studentPlanSectionEnabled).map(
    ({ targetId, title, description, placement, hideNextButton }) => ({
      title,
      description,
      placement,
      target: () => document.getElementById(targetId) as HTMLElement,
      prevButtonProps: { children: 'Back' },
      nextButtonProps: hideNextButton ? { style: { display: 'none' } } : undefined
    })
  )
